import React, { useState, useEffect } from 'react';
import BottomSheet from '../../../shared/ui/forms/BottomSheet';
import ButtonList from '../../../shared/ui/buttons/ButtonList';
import { sitesV2Api, SiteMemberRole, SiteRole } from '../../../api/sitesV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import './ManageMemberRoles.css';

interface ManageMemberRolesProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: number;
  siteMemberId: number;
  userName: string;
  onRolesUpdated?: () => void;
}

const ManageMemberRoles: React.FC<ManageMemberRolesProps> = ({
  isOpen,
  onClose,
  siteId,
  siteMemberId,
  userName,
  onRolesUpdated
}) => {
  const { user } = useAuth();
  const [roles, setRoles] = useState<SiteMemberRole[]>([]);
  const [availableRoles, setAvailableRoles] = useState<SiteRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddRoleSheet, setShowAddRoleSheet] = useState(false);
  const [showRoleHistorySheet, setShowRoleHistorySheet] = useState(false);
  const [roleHistory, setRoleHistory] = useState<SiteMemberRole[]>([]);
  const [selectedRoleToAdd, setSelectedRoleToAdd] = useState<SiteRole | null>(null);
  const [addReason, setAddReason] = useState('');
  const [revokeReason, setRevokeReason] = useState('');
  const [showRevokeSheet, setShowRevokeSheet] = useState(false);
  const [roleToRevoke, setRoleToRevoke] = useState<SiteMemberRole | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, siteId, siteMemberId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesData, availableRolesData] = await Promise.all([
        sitesV2Api.getSiteMemberRoles(siteId, siteMemberId),
        sitesV2Api.getSiteRoles(siteId)
      ]);
      setRoles(rolesData);
      setAvailableRoles(availableRolesData);
    } catch (err: any) {
      console.error('Error loading roles:', err);
      setError('Ошибка загрузки ролей');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const history = await sitesV2Api.getSiteMemberRoleHistory(siteId, siteMemberId);
      setRoleHistory(history);
      setShowRoleHistorySheet(true);
    } catch (err: any) {
      console.error('Error loading role history:', err);
      setError('Ошибка загрузки истории ролей');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = async () => {
    if (!selectedRoleToAdd) return;

    setLoading(true);
    setError(null);
    try {
      await sitesV2Api.assignRole(siteId, siteMemberId, selectedRoleToAdd.id, addReason || undefined);
      await loadData();
      setShowAddRoleSheet(false);
      setSelectedRoleToAdd(null);
      setAddReason('');
      onRolesUpdated?.();
    } catch (err: any) {
      console.error('Error assigning role:', err);
      setError(err.response?.data?.message || 'Ошибка при назначении роли');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeRole = async () => {
    if (!roleToRevoke) return;

    setLoading(true);
    setError(null);
    try {
      await sitesV2Api.revokeRole(siteId, siteMemberId, roleToRevoke.id, revokeReason || undefined);
      await loadData();
      setShowRevokeSheet(false);
      setRoleToRevoke(null);
      setRevokeReason('');
      onRolesUpdated?.();
    } catch (err: any) {
      console.error('Error revoking role:', err);
      setError(err.response?.data?.message || 'Ошибка при отзыве роли');
    } finally {
      setLoading(false);
    }
  };

  const openRevokeSheet = (role: SiteMemberRole) => {
    setRoleToRevoke(role);
    setRevokeReason('');
    setShowRevokeSheet(true);
  };

  const openAddRoleSheet = () => {
    const unassignedRoles = availableRoles.filter(
      ar => !roles.some(r => r.roleId === ar.id)
    );
    
    if (unassignedRoles.length === 0) {
      setError('Все доступные роли уже назначены');
      return;
    }
    
    setShowAddRoleSheet(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUnassignedRoles = () => {
    return availableRoles.filter(ar => !roles.some(r => r.roleId === ar.id));
  };

  return (
    <>
      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        title={`Роли: ${userName}`}
        size="medium"
      >
        <div className="manage-roles-content">
          {error && (
            <div className="error-message">
              <i className="ri-error-warning-line"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Активные роли */}
          <div className="roles-section">
            <div className="roles-section-header">
              <h3>Активные роли ({roles.length})</h3>
              <button
                className="btn-secondary btn-sm"
                onClick={openAddRoleSheet}
                disabled={loading || getUnassignedRoles().length === 0}
              >
                <i className="ri-add-line"></i>
                Добавить роль
              </button>
            </div>

            {loading && roles.length === 0 ? (
              <div className="loading-state">
                <i className="ri-loader-4-line ri-spin"></i>
                <span>Загрузка ролей...</span>
              </div>
            ) : roles.length === 0 ? (
              <div className="empty-state">
                <i className="ri-user-settings-line"></i>
                <p>У участника нет активных ролей</p>
                <button className="btn-primary" onClick={openAddRoleSheet}>
                  Назначить роль
                </button>
              </div>
            ) : (
              <div className="roles-list">
                {roles.map(role => (
                  <div key={role.id} className="role-card">
                    <div className="role-card-main">
                      <div className="role-icon">
                        <i className="ri-user-star-line"></i>
                      </div>
                      <div className="role-info">
                        <div className="role-name">{role.roleName}</div>
                        <div className="role-meta">
                          <span className="role-meta-item">
                            <i className="ri-calendar-line"></i>
                            {formatDate(role.addedAt)}
                          </span>
                          <span className="role-meta-item">
                            <i className="ri-user-line"></i>
                            {role.addedByUserName || 'Неизвестный'}
                          </span>
                        </div>
                        {role.addedReason && (
                          <div className="role-reason">
                            <i className="ri-information-line"></i>
                            {role.addedReason}
                          </div>
                        )}
                      </div>
                      <button
                        className="btn-icon btn-danger-ghost"
                        onClick={() => openRevokeSheet(role)}
                        disabled={loading}
                        title="Отозвать роль"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Кнопка истории */}
          <div className="roles-actions">
            <button
              className="btn-secondary btn-full"
              onClick={loadHistory}
              disabled={loading}
            >
              <i className="ri-history-line"></i>
              Показать историю ролей
            </button>
          </div>
        </div>
      </BottomSheet>

      {/* Bottom Sheet для добавления роли */}
      <BottomSheet
        isOpen={showAddRoleSheet}
        onClose={() => {
          setShowAddRoleSheet(false);
          setSelectedRoleToAdd(null);
          setAddReason('');
          setError(null);
        }}
        title="Назначить роль"
        size="medium"
      >
        <div className="add-role-content">
          {error && (
            <div className="error-message">
              <i className="ri-error-warning-line"></i>
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label>Выберите роль *</label>
            <div className="roles-grid">
              {getUnassignedRoles().map(role => (
                <div
                  key={role.id}
                  className={`role-option ${selectedRoleToAdd?.id === role.id ? 'selected' : ''}`}
                  onClick={() => setSelectedRoleToAdd(role)}
                >
                  <div className="role-option-icon">
                    <i className="ri-user-star-line"></i>
                  </div>
                  <div className="role-option-info">
                    <div className="role-option-name">{role.name}</div>
                    {role.description && (
                      <div className="role-option-description">{role.description}</div>
                    )}
                  </div>
                  {selectedRoleToAdd?.id === role.id && (
                    <i className="ri-checkbox-circle-fill role-option-check"></i>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Причина назначения (опционально)</label>
            <textarea
              value={addReason}
              onChange={(e) => setAddReason(e.target.value)}
              placeholder="Например: Временное назначение на период отпуска..."
              rows={3}
              maxLength={500}
            />
            <div className="char-count">{addReason.length}/500</div>
          </div>

          <div className="modal-actions">
            <button
              className="btn-secondary"
              onClick={() => {
                setShowAddRoleSheet(false);
                setSelectedRoleToAdd(null);
                setAddReason('');
                setError(null);
              }}
              disabled={loading}
            >
              Отмена
            </button>
            <button
              className="btn-primary"
              onClick={handleAddRole}
              disabled={!selectedRoleToAdd || loading}
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line ri-spin"></i>
                  Назначение...
                </>
              ) : (
                <>
                  <i className="ri-check-line"></i>
                  Назначить роль
                </>
              )}
            </button>
          </div>
        </div>
      </BottomSheet>

      {/* Bottom Sheet для отзыва роли */}
      <BottomSheet
        isOpen={showRevokeSheet}
        onClose={() => {
          setShowRevokeSheet(false);
          setRoleToRevoke(null);
          setRevokeReason('');
          setError(null);
        }}
        title="Отозвать роль"
        size="small"
      >
        <div className="revoke-role-content">
          {error && (
            <div className="error-message">
              <i className="ri-error-warning-line"></i>
              <span>{error}</span>
            </div>
          )}

          <div className="warning-message">
            <i className="ri-alert-line"></i>
            <p>
              Вы уверены что хотите отозвать роль <strong>{roleToRevoke?.roleName}</strong>?
            </p>
          </div>

          <div className="form-group">
            <label>Причина отзыва (опционально)</label>
            <textarea
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              placeholder="Например: Роль больше не требуется..."
              rows={3}
              maxLength={500}
            />
            <div className="char-count">{revokeReason.length}/500</div>
          </div>

          <div className="modal-actions">
            <button
              className="btn-secondary"
              onClick={() => {
                setShowRevokeSheet(false);
                setRoleToRevoke(null);
                setRevokeReason('');
                setError(null);
              }}
              disabled={loading}
            >
              Отмена
            </button>
            <button
              className="btn-danger"
              onClick={handleRevokeRole}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line ri-spin"></i>
                  Отзыв...
                </>
              ) : (
                <>
                  <i className="ri-close-circle-line"></i>
                  Отозвать роль
                </>
              )}
            </button>
          </div>
        </div>
      </BottomSheet>

      {/* Bottom Sheet истории ролей */}
      <BottomSheet
        isOpen={showRoleHistorySheet}
        onClose={() => {
          setShowRoleHistorySheet(false);
          setRoleHistory([]);
        }}
        title="История ролей"
        size="large"
      >
        <div className="role-history-content">
          {roleHistory.length === 0 ? (
            <div className="empty-state">
              <i className="ri-history-line"></i>
              <p>История пуста</p>
            </div>
          ) : (
            <div className="timeline">
              {roleHistory.map(role => (
                <div key={role.id} className="timeline-item">
                  <div className="timeline-marker" />
                  <div className="timeline-content">
                    {/* Событие отзыва */}
                    {role.revokedAt && (
                      <div className="timeline-event revoked">
                        <div className="event-header">
                          <span className="event-badge revoked">
                            <i className="ri-close-circle-line"></i>
                            Роль отозвана
                          </span>
                          <span className="event-date">{formatDate(role.revokedAt)}</span>
                        </div>
                        <div className="event-body">
                          <strong>{role.revokedByUserName || 'Неизвестный'}</strong>
                          {' '}лишил роли <strong>{role.roleName}</strong>
                          {role.revokedReason && (
                            <div className="event-reason">
                              <i className="ri-chat-quote-line"></i>
                              {role.revokedReason}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Событие назначения */}
                    <div className="timeline-event assigned">
                      <div className="event-header">
                        <span className="event-badge assigned">
                          <i className="ri-checkbox-circle-line"></i>
                          Роль назначена
                        </span>
                        <span className="event-date">{formatDate(role.addedAt)}</span>
                      </div>
                      <div className="event-body">
                        <strong>{role.addedByUserName || 'Неизвестный'}</strong>
                        {' '}назначил роль <strong>{role.roleName}</strong>
                        {role.addedReason && (
                          <div className="event-reason">
                            <i className="ri-chat-quote-line"></i>
                            {role.addedReason}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Продолжительность */}
                    {role.revokedAt && (
                      <div className="role-duration">
                        <i className="ri-time-line"></i>
                        Роль была активна {calculateDuration(role.addedAt, role.revokedAt)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </BottomSheet>
    </>
  );
};

function calculateDuration(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'менее дня';
  if (diffDays === 1) return '1 день';
  if (diffDays < 30) return `${diffDays} дней`;
  
  const months = Math.floor(diffDays / 30);
  const days = diffDays % 30;
  
  return `${months} мес. ${days > 0 ? days + ' дн.' : ''}`.trim();
}

export default ManageMemberRoles;

